import React, { useMemo, useState } from 'react';
import {
  ArrowRight, Plus, Edit2, Trash2, List, Target, SkipForward, AlertCircle, XCircle, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, X
} from 'lucide-react';
import { htmlToPlainText } from './utils';

const NavigationPanel = ({
  selectedElementData,
  selectedElement,
  screens,
  currentScreenIndex,
  setScreens,
  navigationRules,
  setNavigationRules,
  setShowRuleEditorModal,
  setEditingRule,
  setConfirmDialog,
  setSelectedElement,
  setCurrentScreenIndex
}) => {
  // Gather ALL rules from all elements across all screens.
  // Rules are keyed by element.id in state, so if the same element_id appears
  // on more than one screen the naive traversal would emit each rule once per
  // screen. Dedup by (element.id, rule.id) — the rule itself is screen-agnostic.
  const allRulesWithContext = useMemo(() => {
    const rules = [];
    const seen = new Set();

    screens.forEach((screen, screenIdx) => {
      if (!screen.elements) return;

      screen.elements.forEach(element => {
        const elementRules = navigationRules[element.id] || [];
        if (elementRules.length === 0) return;

        elementRules.forEach((rule, ruleIdx) => {
          const dedupKey = `${element.id}::${rule.id ?? `idx${ruleIdx}`}`;
          if (seen.has(dedupKey)) return;
          seen.add(dedupKey);

          // Get better element name - try content (HTML), questionText, etc.
          const rawContent = element.content || element.questionText || element.data?.text || element.itemData?.text || element.text || element.label || '';
          const cleanName = htmlToPlainText(rawContent).substring(0, 50) || 'Unnamed Question';

          rules.push({
            ...rule,
            elementId: element.id,
            elementName: cleanName,
            elementType: element.type,
            screenIndex: screenIdx,
            screenName: screen.name || `Screen ${screenIdx + 1}`,
            ruleIndex: ruleIdx
          });
        });
      });
    });

    return rules;
  }, [screens, navigationRules]);

  // Split rules into selected element rules and other rules
  const selectedElementRules = useMemo(() => {
    return allRulesWithContext.filter(r => r.elementId === selectedElement);
  }, [allRulesWithContext, selectedElement]);

  const otherRules = useMemo(() => {
    return allRulesWithContext.filter(r => r.elementId !== selectedElement);
  }, [allRulesWithContext, selectedElement]);

  const totalRuleCount = allRulesWithContext.length;

  // Track which rule is expanded for detailed view
  const [expandedRuleId, setExpandedRuleId] = useState(null);

  // Get all question elements for looking up question text
  const allQuestionElements = useMemo(() => {
    const elements = [];
    screens.forEach((screen, screenIdx) => {
      if (!screen.elements) return;
      screen.elements.forEach(el => {
        if (el.type === 'question') {
          // Try multiple property paths for question text (same as RuleEditorModal)
          const rawText = el.content || el.questionText || el.data?.text || el.itemData?.text || el.text || el.label || '';
          const displayText = htmlToPlainText(rawText).substring(0, 80) || 'Question';
          elements.push({
            id: el.id,
            questionId: el.questionId,
            questionText: displayText,
            screenName: screen.name || `Screen ${screenIdx + 1}`
          });
        }
      });
    });
    return elements;
  }, [screens]);

  // Helper to get question text by element ID, frontend questionId, or backend question ID
  const getQuestionText = (id) => {
    if (!id) return 'Question';
    const strId = String(id);
    // Try matching by element id, frontend questionId, or backend database questionId
    const el = allQuestionElements.find(q => 
      q.id === id || 
      q.id === strId ||
      String(q.questionId) === strId ||
      q.questionId === id
    );
    if (el) {
      return truncateName(el.questionText, 50);
    }
    return `Question #${id}`;
  };

  // Helper to format condition text with better detail
  const formatCondition = (rule, verbose = false) => {
    const op = rule.operator === '==' ? '=' : rule.operator;
    if (rule.condition_type === 'answer_value') {
      return { type: 'Answer', detail: `${op} ${rule.value}` };
    }
    if (rule.condition_type === 'cumulative_score') {
      const qCount = rule.question_ids?.length || rule.backend_question_ids?.length || 0;
      return { type: 'Sum', detail: `of ${qCount} Q${qCount !== 1 ? 's' : ''} ${op} ${rule.value}` };
    }
    if (rule.condition_type === 'answer_combination') {
      const condCount = rule.conditions?.length || rule.backend_conditions?.length || 0;
      const logic = rule.logic === 'or' ? 'ANY' : 'ALL';
      return { type: 'Combo', detail: `${condCount} condition${condCount !== 1 ? 's' : ''} (${logic})` };
    }
    return { type: rule.condition_type, detail: `${op} ${rule.value}` };
  };

  // Helper to format action text and pick its icon
  const getActionInfo = (rule) => {
    if (rule.action_type === 'skip_to') return {
      text: `Skip to ${rule.target_screen_name || 'Screen'}`,
      icon: SkipForward
    };
    if (rule.action_type === 'show_alert') return {
      text: 'Show Alert',
      icon: AlertCircle
    };
    if (rule.action_type === 'end_survey') return {
      text: 'End Survey',
      icon: XCircle
    };
    return { text: rule.action_type, icon: ArrowRight };
  };

  // Truncate element name for display
  const truncateName = (name, maxLength = 30) => {
    if (!name) return 'Element';
    const stripped = htmlToPlainText(name);
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  // Handle clicking on a rule to navigate to that element
  const handleRuleClick = (rule) => {
    if (rule.screenIndex !== currentScreenIndex) {
      setCurrentScreenIndex(rule.screenIndex);
    }
    setSelectedElement(rule.elementId);
  };

  // Render a single rule card - with expandable detail view
  const RuleCard = ({ rule, isHighlighted, showElementContext }) => {
    const condition = formatCondition(rule);
    const actionInfo = getActionInfo(rule);
    const ActionIcon = actionInfo.icon;
    const isExpanded = expandedRuleId === rule.id;
    
    const handleCardClick = (e) => {
      if (!isHighlighted) {
        handleRuleClick(rule);
      } else {
        // Toggle expansion
        setExpandedRuleId(isExpanded ? null : rule.id);
      }
    };

    // Get detailed condition info for expanded view
    const getExpandedConditionDetails = () => {
      if (rule.condition_type === 'answer_value') {
        const qIds = rule.question_ids || rule.backend_question_ids || [];
        return {
          title: 'Answer Value Condition',
          items: qIds.length > 0 ? [{
            label: 'Question',
            value: getQuestionText(qIds[0])
          }] : [],
          comparison: `Value ${rule.operator === '==' ? '=' : rule.operator} ${rule.value}`
        };
      }
      if (rule.condition_type === 'cumulative_score') {
        const qIds = rule.question_ids || rule.backend_question_ids || [];
        return {
          title: 'Cumulative Score Condition',
          items: qIds.map((id, i) => ({
            label: `Q${i + 1}`,
            value: getQuestionText(id)
          })),
          comparison: `Total ${rule.operator === '==' ? '=' : rule.operator} ${rule.value}`
        };
      }
      if (rule.condition_type === 'answer_combination') {
        const conditions = rule.conditions || rule.backend_conditions || [];
        return {
          title: `Answer Combination (${rule.logic === 'or' ? 'ANY' : 'ALL'} match)`,
          items: conditions.map((cond, i) => ({
            label: `Condition ${i + 1}`,
            value: `${getQuestionText(cond.question_id)} ${cond.operator === '==' ? '=' : cond.operator} ${cond.value}`
          })),
          comparison: null
        };
      }
      return { title: 'Condition', items: [], comparison: `${rule.operator} ${rule.value}` };
    };
    
    return (
      <div
        className={`card cursor-pointer overflow-hidden transition-[border-color,box-shadow] duration-150 ${
          isHighlighted
            ? rule.enabled !== false
              ? 'border-primary-500 ring-1 ring-primary-500 dark:border-primary-400 dark:ring-primary-400'
              : 'border-gray-300 opacity-70 dark:border-white/15'
            : rule.enabled !== false
              ? 'hover:border-gray-300 dark:hover:border-white/15'
              : 'opacity-60'
        }`}
        onClick={handleCardClick}
      >
        {/* Header row with context */}
        <div className={`flex min-h-[44px] items-center justify-between gap-2 px-3 py-2 ${
          isHighlighted ? 'bg-primary-50/50 dark:bg-primary-400/[0.08]' : ''
        }`}>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {showElementContext ? (
              <>
                <span className="badge badge-gray flex-shrink-0">
                  {rule.condition_type === 'answer_combination' 
                    ? `${(rule.conditions?.length || rule.backend_conditions?.length || 0)} Questions`
                    : rule.condition_type === 'cumulative_score'
                    ? `Sum of ${(rule.question_ids?.length || rule.backend_question_ids?.length || 0)}`
                    : 'Answer'}
                </span>
                <span className="truncate text-[12.5px] font-medium text-gray-700 dark:text-gray-300">
                  {actionInfo.text}
                </span>
              </>
            ) : (
              <span className={`text-[12.5px] font-semibold ${isHighlighted ? 'text-primary-700 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'}`}>
                Rule {rule.ruleIndex + 1}
              </span>
            )}
            {isHighlighted && (
              <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </span>
            )}
          </div>
          {isHighlighted && (
            <div className="flex flex-shrink-0 items-center gap-0.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const updatedRules = [...navigationRules[selectedElement]];
                  updatedRules[rule.ruleIndex] = { ...rule, enabled: rule.enabled === false ? true : false };
                  setNavigationRules({
                    ...navigationRules,
                    [selectedElement]: updatedRules
                  });
                }}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${rule.enabled !== false ? 'text-primary-600 hover:bg-primary-50/80 dark:text-primary-400 dark:hover:bg-primary-400/10' : 'text-gray-400 hover:bg-gray-100/80 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-gray-200'}`}
                title={rule.enabled !== false ? 'Disable rule' : 'Enable rule'}
              >
                {rule.enabled !== false ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingRule(rule);
                  setShowRuleEditorModal(true);
                }}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100/80 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-gray-100"
                title="Edit rule"
              >
                <Edit2 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog({
                    isOpen: true,
                    title: 'Delete Rule',
                    message: 'Are you sure you want to delete this navigation rule?',
                    type: 'danger',
                    onConfirm: () => {
                      const updatedRules = navigationRules[selectedElement].filter((_, i) => i !== rule.ruleIndex);
                      setNavigationRules({
                        ...navigationRules,
                        [selectedElement]: updatedRules
                      });
                      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                    }
                  });
                }}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-danger-50 hover:text-danger-600 dark:text-gray-400 dark:hover:bg-danger-500/10 dark:hover:text-danger-400"
                title="Delete rule"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        
        {/* Collapsed view - Condition and Action summary */}
        {!isExpanded && (
          <div className="space-y-1.5 border-t border-gray-100 px-3 py-2.5 dark:border-white/[0.06]">
            <div className="flex items-center gap-2">
              <span className="w-6 flex-shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500">If</span>
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="badge badge-gray flex-shrink-0">
                  {condition.type}
                </span>
                <span className="truncate text-xs tabular-nums text-gray-700 dark:text-gray-300">{condition.detail}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 flex-shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500">→</span>
              <span className="badge badge-gray min-w-0 max-w-full">
                <ActionIcon className="h-3 w-3 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                <span className="truncate">{actionInfo.text}</span>
              </span>
            </div>
          </div>
        )}

        {/* Expanded view - Full details */}
        {isExpanded && isHighlighted && (
          <div className="space-y-3 border-t border-gray-100 px-3 py-3 dark:border-white/[0.06]">
            {(() => {
              const details = getExpandedConditionDetails();
              return (
                <>
                  <div>
                    <p className="mb-2 text-xs font-semibold text-gray-900 dark:text-gray-50">{details.title}</p>
                    {details.items.length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {details.items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs leading-relaxed">
                            <span className="shrink-0 font-medium text-gray-500 dark:text-gray-400">{item.label}:</span>
                            <span className="break-words text-gray-800 dark:text-gray-200">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {details.comparison && (
                      <div className="badge badge-gray tabular-nums">
                        {details.comparison}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-100 pt-2.5 dark:border-white/[0.06]">
                    <p className="mb-2 text-xs font-semibold text-gray-900 dark:text-gray-50">Action</p>
                    <div className="inline-flex max-w-full items-start gap-1.5 rounded-lg bg-gray-100/80 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10 dark:bg-white/[0.06] dark:text-gray-300 dark:ring-white/10">
                      <ActionIcon className="mt-px h-3.5 w-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                      {actionInfo.text}
                    </div>
                    {rule.action_type === 'skip_to' && rule.target_screen_index !== undefined && (
                      <p className="mt-1.5 text-[11.5px] tabular-nums text-gray-500 dark:text-gray-400">Screen index: {rule.target_screen_index + 1}</p>
                    )}
                  </div>

                  {rule.enabled === false && (
                    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                      <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
                      This rule is currently disabled
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // Check if selected element can have rules (question or button with skip action)
  const canSelectedElementHaveRules = selectedElementData && (
    selectedElementData.type === 'question' || 
    (selectedElementData.type === 'button' && selectedElementData.action === 'skip')
  );

  return (
    <div className="space-y-3 p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-0.5">
        <div className="flex min-w-0 items-center gap-2">
          <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          <h3 className="truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Navigation Rules</h3>
        </div>
        <span className="badge badge-gray flex-shrink-0 tabular-nums">{totalRuleCount} rule{totalRuleCount !== 1 ? 's' : ''}</span>
      </div>

      {/* Add Rule Button */}
      <button
        className="btn btn-primary btn-sm w-full"
        onClick={() => setShowRuleEditorModal(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Add Rule
      </button>

      {/* Simple flat list of all rules */}
      {totalRuleCount === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-300/80 px-4 py-6 text-center text-[13px] leading-relaxed text-gray-500 dark:border-white/10 dark:text-gray-400">
          No rules yet. Add a rule to control survey navigation.
        </p>
      ) : (
        <div className="space-y-2">
          {allRulesWithContext.map((rule, index) => (
            <RuleCard 
              key={`rule-${rule.elementId}-${rule.ruleIndex}-${index}`} 
              rule={rule} 
              isHighlighted={rule.elementId === selectedElement}
              showElementContext={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NavigationPanel;
